<template>
  <div class="tricks-page container">
    <TheBreadcrumb :pages="arianeRoute" />
    <HeaderCommunity :page="currentPageInfo" />
    <SearchResults
      :is-loading="isLoading"
      placeholder-text="Rechercher une astuce"
      @search-update="searchUpdateHandler"
    >
      <template #title>
        <FilterSub
          :current="filterSubCategory"
          :items="communityTricksSubcategoriesList"
          :action="changeFilterTo"
          inside-header
        />
      </template>
      <template #results>
        <CardContainer
          :items="communityTricks"
          :no-result-text="noContent"
        />
      </template>
    </SearchResults>
    <ThePagination
      :total-pages="getPagesCount"
      :current-page="getCurrentPage"
      :go-to="changePageTo"
    />
    <PegiWarning v-if="community?.pegi === '18'" />
  </div>
</template>

<script setup lang="ts">
definePageMeta({
  middleware: async (to) => {
    const communityStore = useCommunitiesStore()
    const tricksStore = useTricksStore()
    const communityName = to.params.communityName as string
    await Promise.all([
      // Fetch data about the community
      communityStore.fetchCommunity({
        slug: communityName,
      }),
      // Fetch tricks categories from the community
      tricksStore.fetchTricksCommunitySubcategories(communityName),
      tricksStore.fetchCommunityTricks({
        community: communityName,
      }),
    ])

    if (tricksStore.getPagesCount === -1) {
      return navigateTo(to.path, { replace: true })
    }
  },
})

const tricksStore = useTricksStore()
const {
  getPagesCount,
  getCurrentPage,
  communityTricksSubcategoriesList,
  communityTricks,
} = storeToRefs(tricksStore)

const communitiesStore = useCommunitiesStore()
const { community } = storeToRefs(communitiesStore)

const arianeRoute = computed(() => {
  return [
    {
      title: 'Toutes les communautés',
      route: '/communautes',
    },
    {
      title: community.value?.title,
      route: `/communautes/${community.value?.slug}`,
    },
    {
      title: 'Les astuces',
      route: '',
    },
  ]
})

const breadcrumbSchema = useBreadcrumbSchema({
  arianeRoute,
})

const link = useLinkPrevNext({
  currentPage: getCurrentPage,
  pagesCount: getPagesCount,
})

useHead({
  title: 'Les astuces',
  link,
  script: [breadcrumbSchema],
})

useSeoMeta({
  title: `Toutes les astuces ${community.value?.title}`,
  description: `Découvrez toutes les solutions, tips et astuces de la communauté sur ${community.value?.title}.`,
  ogTitle: `Toutes les astuces ${community.value?.title}`,
  ogDescription: `Découvrez toutes les solutions, tips et astuces de la communauté sur ${community.value?.title}.`,
  ogImage:
    community.value?.image_desktop?.url ??
    'https://www.weareplaystation.fr/img/og-img.jpg',
})

const route = useRoute()
const slug = computed(() => route.params.communityName as string)
const filterSubCategory = ref('all')

const referentialStore = useReferentialStore()
const { referential } = storeToRefs(referentialStore)

const mediaQuery = useCustomMediaQuery()

const currentPageInfo = computed(() => {
  const image = getImage(community.value, mediaQuery)
  return {
    title: 'Les astuces',
    game: community.value?.title,
    pegi: community.value?.pegi,
    members: community.value?.users_count,
    image,
    description: `Découvrez toutes les solutions, tips et astuces  de la communauté sur ${community.value?.title}`,
    contribPoints: referential.value?.points.post_source_create,
  }
})

const searchData = async () => {
  isLoading.value = true

  const filter =
    filterSubCategory.value !== 'all' ? filterSubCategory.value : ''

  await tricksStore.fetchCommunityTricks({
    page: 1,
    search: search.value,
    filter,
    community: slug.value,
  })

  if (!communityTricks.value?.length) {
    noContent.value = !search.value
      ? 'Aucun contenu'
      : 'Aucun résultat pour votre recherche'
  }

  isLoading.value = false
}

const { search, isLoading, noContent, searchUpdateHandler } = useSearching({
  searchData,
})

const changePageTo = async (page: number) => {
  if (page > 1) {
    const filter =
      filterSubCategory.value !== 'all' ? filterSubCategory.value : undefined

    await tricksStore.fetchCommunityTricks({
      page,
      search: search.value,
      filter,
      community: slug.value,
    })
  }
}

const changeFilterTo = async (subCategory: string) => {
  filterSubCategory.value = subCategory
  const filter =
    filterSubCategory.value !== 'all' ? filterSubCategory.value : undefined

  await tricksStore.fetchCommunityTricks({
    page: 1,
    search: search.value,
    filter,
    community: slug.value,
  })
}
</script>

<style lang="scss" scoped>
.tricks-page {
  min-height: 100vh;
  padding-bottom: 10rem;
}
</style>
